/**
 * Budget Calculations for PlanLens
 * Core calculation logic for budget analysis
 */

import { normalizeToMonthly, getCostType } from './frequencyCalculations';

/**
 * Calculate total monthly income from budget items
 * @param {Array} budgetItems - Array of budget items
 * @returns {number} Total monthly income
 */
export const calculateTotalMonthlyIncome = (budgetItems) => {
  if (!budgetItems || budgetItems.length === 0) return 0;

  return budgetItems
    .filter(item => item.type === 'income')
    .reduce((sum, item) => {
      const monthly = normalizeToMonthly(item.amount, item.frequency);
      return sum + monthly;
    }, 0);
};

/**
 * Calculate total monthly expenses from budget items
 * @param {Array} budgetItems - Array of budget items
 * @returns {number} Total monthly expenses
 */
export const calculateTotalMonthlyExpenses = (budgetItems) => {
  if (!budgetItems || budgetItems.length === 0) return 0;

  return budgetItems
    .filter(item => item.type === 'expense')
    .reduce((sum, item) => {
      const monthly = normalizeToMonthly(item.amount, item.frequency);
      return sum + monthly;
    }, 0);
};

/**
 * Calculate total monthly savings contributions
 * @param {Array} budgetItems - Array of budget items
 * @returns {number} Total monthly savings
 */
export const calculateTotalMonthlySavings = (budgetItems) => {
  if (!budgetItems || budgetItems.length === 0) return 0;

  return budgetItems
    .filter(item => item.type === 'savings')
    .reduce((sum, item) => {
      const monthly = normalizeToMonthly(item.amount, item.frequency);
      return sum + monthly;
    }, 0);
};

/**
 * Calculate monthly surplus (income - expenses - savings)
 * @param {Array} budgetItems - Array of budget items
 * @returns {Object} { income, expenses, savings, surplus }
 */
export const calculateMonthlySurplus = (budgetItems) => {
  const income = calculateTotalMonthlyIncome(budgetItems);
  const expenses = calculateTotalMonthlyExpenses(budgetItems);
  const savings = calculateTotalMonthlySavings(budgetItems);

  return {
    income,
    expenses,
    savings,
    surplus: income - expenses - savings,
    totalOutgoings: expenses + savings
  };
};

/**
 * Calculate savings rate as percentage of income
 * @param {Array} budgetItems - Array of budget items
 * @returns {number} Savings rate percentage (0-100)
 */
export const calculateSavingsRate = (budgetItems) => {
  const { income, surplus, savings } = calculateMonthlySurplus(budgetItems);

  if (income === 0) return 0;

  const totalSavings = surplus + savings;
  return (totalSavings / income) * 100;
};

/**
 * Calculate fixed vs variable costs
 * @param {Array} budgetItems - Array of budget items with category info
 * @returns {Object} { fixed, variable, fixedPercentage, variablePercentage }
 */
export const calculateFixedVsVariable = (budgetItems) => {
  const expenses = budgetItems.filter(item => item.type === 'expense');

  const fixed = expenses
    .filter(item => getCostType(item.categoryName || item.name) === 'fixed')
    .reduce((sum, item) => sum + normalizeToMonthly(item.amount, item.frequency), 0);

  const variable = expenses
    .filter(item => getCostType(item.categoryName || item.name) === 'variable')
    .reduce((sum, item) => sum + normalizeToMonthly(item.amount, item.frequency), 0);

  const total = fixed + variable;

  return {
    fixed,
    variable,
    fixedPercentage: total > 0 ? (fixed / total) * 100 : 0,
    variablePercentage: total > 0 ? (variable / total) * 100 : 0
  };
};

/**
 * Calculate spending by category
 * @param {Array} budgetItems - Array of budget items with category info
 * @returns {Array} Array of { categoryId, categoryName, amount, percentage }
 */
export const calculateCategoryTotals = (budgetItems) => {
  const totalExpenses = calculateTotalMonthlyExpenses(budgetItems);

  const categoryMap = {};

  budgetItems
    .filter(item => item.type === 'expense')
    .forEach(item => {
      const categoryId = item.categoryId || 'uncategorized';
      const categoryName = item.categoryName || 'Uncategorized';
      const monthly = normalizeToMonthly(item.amount, item.frequency);

      if (!categoryMap[categoryId]) {
        categoryMap[categoryId] = {
          categoryId,
          categoryName,
          amount: 0,
          items: []
        };
      }

      categoryMap[categoryId].amount += monthly;
      categoryMap[categoryId].items.push(item);
    });

  return Object.values(categoryMap)
    .map(cat => ({
      ...cat,
      percentage: totalExpenses > 0 ? (cat.amount / totalExpenses) * 100 : 0
    }))
    .sort((a, b) => b.amount - a.amount);
};

/**
 * Calculate spending by group (Income, Household Bills, Living Costs, etc.)
 * @param {Array} budgetItems - Array of budget items with group info
 * @returns {Array} Array of { groupName, amount, percentage, categories }
 */
export const calculateGroupTotals = (budgetItems) => {
  const totalExpenses = calculateTotalMonthlyExpenses(budgetItems);

  const groupMap = {};

  budgetItems
    .filter(item => item.type === 'expense')
    .forEach(item => {
      const groupName = item.groupName || 'Other';
      const monthly = normalizeToMonthly(item.amount, item.frequency);

      if (!groupMap[groupName]) {
        groupMap[groupName] = {
          groupName,
          amount: 0,
          items: []
        };
      }

      groupMap[groupName].amount += monthly;
      groupMap[groupName].items.push(item);
    });

  return Object.values(groupMap)
    .map(group => ({
      ...group,
      percentage: totalExpenses > 0 ? (group.amount / totalExpenses) * 100 : 0
    }))
    .sort((a, b) => b.amount - a.amount);
};

/**
 * Calculate variance between planned budget and actual transactions
 * @param {Array} budgetItems - Array of budget items
 * @param {Array} transactions - Array of transactions for the period
 * @returns {Array} Budget items with variance data
 */
export const calculateBudgetVariance = (budgetItems, transactions) => {
  // Group transactions by category
  const transactionTotals = {};

  transactions.forEach(txn => {
    const categoryId = txn.categoryId || 'uncategorized';
    if (!transactionTotals[categoryId]) {
      transactionTotals[categoryId] = 0;
    }
    transactionTotals[categoryId] += Math.abs(txn.amount);
  });

  // Calculate variance for each budget item
  return budgetItems.map(item => {
    const planned = normalizeToMonthly(item.amount, item.frequency);
    const actual = transactionTotals[item.categoryId] || 0;
    const variance = actual - planned;
    const variancePercentage = planned > 0 ? (variance / planned) * 100 : 0;

    return {
      ...item,
      planned,
      actual,
      variance,
      variancePercentage,
      status: variance > 0 ? 'over' : variance < 0 ? 'under' : 'on-track'
    };
  });
};

/**
 * Calculate monthly summary statistics
 * @param {Array} budgetItems - Array of budget items
 * @param {Array} transactions - Array of transactions
 * @returns {Object} Summary statistics
 */
export const calculateMonthlySummary = (budgetItems, transactions = []) => {
  const { income, expenses, savings, surplus, totalOutgoings } = calculateMonthlySurplus(budgetItems);
  const savingsRate = calculateSavingsRate(budgetItems);
  const { fixed, variable, fixedPercentage, variablePercentage } = calculateFixedVsVariable(budgetItems);

  // Calculate actual spending from transactions
  const actualSpending = transactions
    .filter(txn => txn.amount < 0) // Expenses are negative
    .reduce((sum, txn) => sum + Math.abs(txn.amount), 0);

  return {
    totalMonthlyIncome: income,
    totalMonthlyExpenses: expenses,
    totalMonthlySavings: savings,
    totalOutgoings,
    surplus,
    savingsRate,
    fixedCostsMonthly: fixed,
    variableCostsMonthly: variable,
    fixedPercentage,
    variablePercentage,
    actualSpending,
    spendingVariance: actualSpending - expenses,
    spendingVariancePercentage: expenses > 0 ? ((actualSpending - expenses) / expenses) * 100 : 0
  };
};

/**
 * Calculate budget health score (0-100)
 * @param {Array} budgetItems - Array of budget items
 * @returns {Object} { score, factors }
 */
export const calculateBudgetHealthScore = (budgetItems) => {
  const { income, expenses, surplus } = calculateMonthlySurplus(budgetItems);
  const savingsRate = calculateSavingsRate(budgetItems);
  const { fixedPercentage } = calculateFixedVsVariable(budgetItems);

  const factors = [];
  let score = 0;

  // Factor 1: Positive cash flow (30 points)
  if (surplus > 0) {
    const cashFlowScore = Math.min(30, (surplus / income) * 100);
    score += cashFlowScore;
    factors.push({
      name: 'Positive Cash Flow',
      score: Math.round(cashFlowScore),
      maxScore: 30,
      status: 'good'
    });
  } else {
    factors.push({
      name: 'Positive Cash Flow',
      score: 0,
      maxScore: 30,
      status: 'poor'
    });
  }

  // Factor 2: Savings rate (40 points)
  // Target: 20% savings rate = 40 points
  const savingsScore = Math.min(40, (savingsRate / 20) * 40);
  score += savingsScore;
  factors.push({
    name: 'Savings Rate',
    score: Math.round(savingsScore),
    maxScore: 40,
    status: savingsRate >= 15 ? 'good' : savingsRate >= 10 ? 'fair' : 'poor'
  });

  // Factor 3: Fixed cost ratio (30 points)
  // Target: Fixed costs < 50% of expenses = 30 points
  const fixedRatioScore = fixedPercentage <= 50 ? 30 : Math.max(0, 30 - (fixedPercentage - 50));
  score += fixedRatioScore;
  factors.push({
    name: 'Fixed Cost Ratio',
    score: Math.round(fixedRatioScore),
    maxScore: 30,
    status: fixedPercentage <= 50 ? 'good' : fixedPercentage <= 70 ? 'fair' : 'poor'
  });

  return {
    score: Math.round(score),
    rating: score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Needs Improvement',
    factors
  };
};

/**
 * Compare budget to previous period
 * @param {Object} currentSummary - Current period summary
 * @param {Object} previousSummary - Previous period summary
 * @returns {Object} Comparison data with changes
 */
export const compareBudgetPeriods = (currentSummary, previousSummary) => {
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  return {
    incomeChange: calculateChange(
      currentSummary.totalMonthlyIncome,
      previousSummary.totalMonthlyIncome
    ),
    expenseChange: calculateChange(
      currentSummary.totalMonthlyExpenses,
      previousSummary.totalMonthlyExpenses
    ),
    savingsChange: calculateChange(
      currentSummary.totalMonthlySavings,
      previousSummary.totalMonthlySavings
    ),
    surplusChange: currentSummary.surplus - previousSummary.surplus
  };
};

/**
 * Calculate recommended emergency fund based on expenses
 * @param {Array} budgetItems - Array of budget items
 * @returns {Object} { recommended, months }
 */
export const calculateEmergencyFundTarget = (budgetItems) => {
  const { expenses } = calculateMonthlySurplus(budgetItems);
  const months = 6; // Standard recommendation

  return {
    recommended: expenses * months,
    months,
    monthlyExpenses: expenses
  };
};

/**
 * Calculate debt-to-income ratio
 * @param {Array} budgetItems - Array of budget items (must include debt payments)
 * @returns {number} DTI percentage
 */
export const calculateDebtToIncomeRatio = (budgetItems) => {
  const income = calculateTotalMonthlyIncome(budgetItems);

  const debtPayments = budgetItems
    .filter(item =>
      item.type === 'expense' &&
      (item.categoryName || '').toLowerCase().includes('repayment')
    )
    .reduce((sum, item) => sum + normalizeToMonthly(item.amount, item.frequency), 0);

  return income > 0 ? (debtPayments / income) * 100 : 0;
};

/**
 * Get budget recommendations based on analysis
 * @param {Array} budgetItems - Array of budget items
 * @returns {Array} Array of recommendation objects
 */
export const getBudgetRecommendations = (budgetItems) => {
  const recommendations = [];
  const { income, expenses, surplus } = calculateMonthlySurplus(budgetItems);
  const savingsRate = calculateSavingsRate(budgetItems);
  const categoryTotals = calculateCategoryTotals(budgetItems);

  // Low savings rate
  if (savingsRate < 10) {
    recommendations.push({
      type: 'savings',
      priority: 'high',
      message: `Your savings rate is ${savingsRate.toFixed(1)}%. Aim for at least 10-20% of income.`,
      action: 'Review variable expenses to identify potential savings.'
    });
  }

  // Negative cash flow
  if (surplus < 0) {
    recommendations.push({
      type: 'cashflow',
      priority: 'critical',
      message: `You have a monthly shortfall of ${Math.abs(surplus).toFixed(0)}.`,
      action: 'Reduce expenses or increase income to balance your budget.'
    });
  }

  // High category spending (>30% on single category)
  const highSpendingCategory = categoryTotals.find(cat => cat.percentage > 30);
  if (highSpendingCategory) {
    recommendations.push({
      type: 'category',
      priority: 'medium',
      message: `${highSpendingCategory.categoryName} represents ${highSpendingCategory.percentage.toFixed(1)}% of expenses.`,
      action: `Consider reviewing ${highSpendingCategory.categoryName} for optimization opportunities.`
    });
  }

  return recommendations;
};
